import { goldMaster } from '../state/tenant-state';
import authService from '../features/auth/auth-service';
import { CacheService } from './cache-service';

export type QueryKey = 'sort' | 'size' | 'page' | 'search';

export type Query = {
  [key in QueryKey]?: number | string;
};

export type Options = {
  path: string;
  body?: any;
  query?: Query;
};

export interface Pageable<T> {
  content: T[];
  size: number;
  totalPages: number;
  totalElements: number;
  numberOfElements: number;
  number: number;
  empty?: boolean;
}

export class BaseService<T> {
  path: string;

  constructor(path: string) {
    this.path = path;
  }

  fetch(query: Query): Promise<Pageable<T>> {
    return this.apiFetch({ path: this.path, query }) as Promise<Pageable<T>>;
  }

  get(id: string): Promise<T> {
    return this.apiGet({ path: `${this.path}/${id}` });
  }

  async delete(id: string): Promise<void> {
    await this.apiDelete({ path: `${this.path}/${id}` });
  }

  async create(payload: T): Promise<T> {
    return await this.apiPost({ path: this.path, body: payload });
  }

  async update(id: string, payload: T): Promise<T> {
    return await this.apiPut({ path: `${this.path}/${id}`, body: payload });
  }

  async apiFetch(options: Options): Promise<Pageable<T> | T[]> {
    await authService.refreshToken();
    const result = await fetch(
      `${CacheService.get('pdc.tenant.api')}${options.path}${this.buildQuery(options.query)}`,
      {
        headers: {
          Authorization: `Bearer ${authService.getAccessToken()}`,
          ...this.getTenantHeader()
        }
      }
    );
    return await result.json() as Pageable<T>;
  }

  async apiGet(options: Options): Promise<T> {
    await authService.refreshToken();
    const result = await fetch(
      `${CacheService.get('pdc.tenant.api')}${options.path}${this.buildQuery(options.query)}`,
      {
        headers: {
          Authorization: `Bearer ${authService.getAccessToken()}`,
          ...this.getTenantHeader()
        }
      }
    );
    return await result.json() as T;
  }

  async apiDelete(options: Options): Promise<void> {
    await authService.refreshToken();
    await fetch(
      `${CacheService.get('pdc.tenant.api')}${options.path}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authService.getAccessToken()}`,
          ...this.getTenantHeader()
        }
      }
    );
  }

  async apiPost(options: Options): Promise<T> {
    await authService.refreshToken();
    const result = await fetch(
      `${CacheService.get('pdc.tenant.api')}${options.path}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authService.getAccessToken()}`,
          ...this.getTenantHeader()
        },
        body: JSON.stringify(options.body)
      }
    );
    return await result.json() as T;
  }

  async apiPut(options: Options): Promise<T> {
    await authService.refreshToken();
    const result = await fetch(
      `${CacheService.get('pdc.tenant.api')}${options.path}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authService.getAccessToken()}`,
          ...this.getTenantHeader()
        },
        body: JSON.stringify(options.body)
      }
    );
    return await result.json() as T;
  }

  private getTenantHeader(): { 'X-Tenant': string; } | null {
    const tenantId = CacheService.get('pdc.tenant.id');
    if (tenantId && tenantId !== goldMaster) {
      return { 'X-Tenant': tenantId };
    }
    return null;
  }

  private buildQuery(pagination?: Query): string {
    if (!pagination) {
      return '';
    }
    let query = '?';
    for (const key in pagination) {
      if (query.length > 1) {
        query += '&';
      }
      query += `${key}=${pagination[key as QueryKey] as string}`;
    }
    return query;
  }
}
